import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { Button } from '@material-tailwind/react';
import defaultBackground from '../../assets/Record/recordprint.jpg';

interface LayoutText {
  custom_id: string;
  text: string;
  x: number;
  y: number;
  font: string;
  color: string;
}

interface LayoutConfig {
  texts: LayoutText[];
  bg_image?: string;
}

type RecordData = {
  Competition: string;
  Bib: string;
  Name: string;
  Division: string;
  Time: string;
};

const RecordPhotoPrint: React.FC = () => {
  const { bibNum } = useParams<{ bibNum?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = (location.state as any) || {};

  // 참가자 정보
  const [userName] = useState(stateData.userName || '이름 없음');
  const [userBib] = useState(stateData.compBib || (bibNum || '-'));
  const [compDivLs] = useState(stateData.compDivLs || 'Full');
  const [compRecord] = useState(stateData.compRecord || '기록이 없습니다.');
  const [compCodeState, setCompCodeState] = useState<number | null>(stateData.compCode || null);
  useEffect(() => {
    if (stateData.compCode) setCompCodeState(stateData.compCode);
  }, [stateData.compCode]);

  // 레이아웃 로드
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null);
  useEffect(() => {
    if (compCodeState) {
      axios.get(`/api/get-layout/${compCodeState}?mode=photo`)
        .then(res => {
          const raw = res.data;
          let texts: LayoutText[] = [];
          if (raw.layout_config) {
            try { texts = JSON.parse(raw.layout_config).texts || []; } catch {}
          }
          setLayoutConfig({ bg_image: raw.bg_image, texts });
        })
        .catch(console.error);
    }
  }, [compCodeState]);

  // 배경 URL
  const backgroundUrl = layoutConfig?.bg_image
    ? `/static/images/${layoutConfig.bg_image}`
    : defaultBackground;

  // A4 캔버스 크기 (portrait/landscape)
  const [canvasWidth, setCanvasWidth] = useState(595);
  const [canvasHeight, setCanvasHeight] = useState(842);
  useEffect(() => {
    const img = new Image();
    img.src = backgroundUrl;
    img.onload = () => {
      if (img.naturalWidth > img.naturalHeight) {
        setCanvasWidth(842);
        setCanvasHeight(595);
      } else {
        setCanvasWidth(595);
        setCanvasHeight(842);
      }
    };
  }, [backgroundUrl]);

  // 서버 이미지 갤러리 등
  const [serverImages, setServerImages] = useState<string[]>([]);
  useEffect(() => {
    if (userBib && compCodeState) {
      axios.get<string[]>(`/api/get-bib-images/${compCodeState}/${userBib}`)
        .then(res => setServerImages(res.data))
        .catch(console.error);
    }
  }, [userBib, compCodeState]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const pageSize = 2;
  const visible = serverImages.slice(currentIndex, currentIndex + pageSize);
  const prevPage = () => setCurrentIndex(i => Math.max(i - pageSize, 0));
  const nextPage = () => setCurrentIndex(i => Math.min(i + pageSize, serverImages.length - pageSize));

  const [modalOpen, setModalOpen] = useState(false);
  const [modalUrl, setModalUrl] = useState('');
  const [zoom, setZoom] = useState(1);
  const openModal = (url: string) => { setModalUrl(url); setZoom(1); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  // 업로드
  const [tempImageUrl, setTempImageUrl] = useState('');
  const [userImageUrl, setUserImageUrl] = useState('');
  const [userImageTop, setUserImageTop] = useState('50%');
  const canvasRef = useRef<HTMLDivElement>(null);

  const handleTempFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setTempImageUrl(URL.createObjectURL(file));
  };
  const handleUploadClick = () => {
    if (tempImageUrl) {
      setUserImageUrl(tempImageUrl);
      setTempImageUrl('');
    }
  };

  // 다운로드
  const handleDownloadImage = async (url: string) => {
    try {
      const resp = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = url.split('/').pop() || 'image.jpg';
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch {}
  };

  const captureCanvas = (callback: (dataUrl: string) => void) => {
    if (canvasRef.current) {
      html2canvas(canvasRef.current, {
        scale: 4,
        useCORS: true,
        onclone: (clonedDoc) => {
          const overlays = clonedDoc.querySelectorAll('.overlay-text');
          overlays.forEach(el => {
            const computedStyle = window.getComputedStyle(el);
            const currentTop = computedStyle.top;
            const topVal = parseFloat(currentTop);
            // 텍스트 위치를 위로 10px 올림
            (el as HTMLElement).style.top = (topVal - 10) + "px";
          });
        }
      }).then(canvas => {
        const dataUrl = canvas.toDataURL("image/png");
        callback(dataUrl);
      });
    }
  };

  const handleDownload = () => {
    captureCanvas((dataUrl) => {
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "모바일기록증.png";
      link.click();
    });
  };

  const handleDownloadCert = () => {
    if (canvasRef.current) {
      html2canvas(canvasRef.current, { scale: 4, useCORS: true })
        .then(canv => {
          const link = document.createElement('a');
          link.href = canv.toDataURL('image/png');
          link.download = '사진기록증.png';
          link.click();
        });
    }
  };
    // 기록증 뷰 초기화
  const handleReset = () => {
    setUserImageUrl('');
    setTempImageUrl('');
  };
  if (!userImageUrl && layoutConfig === null) {
    return (
      <div className="flex justify-center items-start h-screen bg-white pt-40">
        <h1 className="text-2xl md:text-4xl font-bold text-center text-red-600">
          이번 대회에서는 사진 기록증을 서비스하지 않습니다.
        </h1>
      </div>
    );
  }
  // 초기 화면: 갤러리 + 업로드
  if (!userImageUrl) {
    const imgSize = 320;
    return (
      <div className="flex flex-col items-center">
        <div className="flex items-center mb-4">
          <Button size="sm" onClick={prevPage} disabled={!visible.length || currentIndex === 0}>◀</Button>
          <div className="flex mx-2">
            {visible.map((fn, idx) => {
              const url = `/static/images/${compCodeState}/${fn}`;
              return (
                <div key={idx} className="flex flex-col items-center m-2">
                  <img src={url} alt="gallery"
                    style={{ width: imgSize, height: 'auto', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => openModal(url)}
                  />
                  <div className="mt-2 flex space-x-2">
                    <Button size="sm" onClick={() => setUserImageUrl(url)}>기록증 만들기</Button>
                    <Button size="sm" onClick={() => handleDownloadImage(url)}>다운로드</Button>
                  </div>
                </div>
              );
            })}
          </div>
          <Button size="sm" onClick={nextPage} disabled={currentIndex + pageSize >= serverImages.length}>▶</Button>
        </div>
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative flex justify-center items-center max-w-[80vw] max-h-[80vh]">
              <img src={modalUrl} alt="enlarged"
                style={{ maxWidth: '50%', maxHeight: '50%', transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                onWheel={e => { e.preventDefault(); setZoom(z => Math.min(Math.max(z + (e.deltaY>0?-.1:.1),.5),3)); }}
              />
              <div className="absolute bottom-2 center-2 flex space-x-2">
                <Button size="sm" onClick={() => setZoom(z=>Math.min(z+.2,3))}>+</Button>
                <Button size="sm" onClick={() => setZoom(z=>Math.max(z-.2,.5))}>-</Button>
                <Button size="sm" onClick={closeModal}>닫기</Button>
              </div>
            </div>
          </div>
        )}
        <div className="w-full px-6 py-4 mt-6" style={{ backgroundColor: '#e5f1ff' }}>
          <p className="text-center mb-3 font-medium">현장 촬영 사진으로 기록증 등록 가능합니다.</p>
          <div className="flex items-center justify-center space-x-4">
            <span className="font-medium">사진 등록</span>
            <input type="file" accept="image/*" onChange={handleTempFileChange} />
            <Button variant="filled" color="blue" onClick={handleUploadClick} disabled={!tempImageUrl}>올리기</Button>
            <Button variant="outlined" onClick={() => navigate(-1)}>이전</Button>
          </div>
        </div>
      </div>
    );
  }

  // 기록증 뷰
  const mapping: Record<string,string> = { name:userName, bib:userBib, div:compDivLs, time:compRecord };
  return (
    <div className="flex flex-col items-center">
      <div ref={canvasRef} className="mt-8 mb-4" style={{ position:'relative', width:canvasWidth, height:canvasHeight, backgroundColor:'#fff' }}>
        <img src={backgroundUrl} alt="background"
          style={{ position:'absolute', width:'100%', height:'auto', top:0, left:0, objectFit:'contain' }}
        />
        {layoutConfig?.texts.map((item,idx)=><div key={idx} className="overlay-text" style={{ position:'absolute', left:item.x, top:item.y,transform: 'translate(-50%, 0)', textAlign: 'center', fontFamily:item.font.split(' ')[0], fontSize:parseInt(item.font.split(' ')[1])||20, color:item.color, whiteSpace:'nowrap' }}>{mapping[item.custom_id]||item.text}</div>)}
        <div style={{ position:'absolute', left:100, top:userImageTop, width:190, overflow:'hidden', border:'2px solid #fff', boxShadow:'0 0 8px rgba(0,0,0,0.5)' }}>
          <img src={userImageUrl} alt="user" style={{ width:'100%', height:'auto', objectFit:'cover' }} onLoad={e=>{const{naturalWidth,naturalHeight}=e.currentTarget; setUserImageTop(naturalWidth>naturalHeight?'40%':'32%');}}/>
        </div>
      </div>
      <div className="flex space-x-4 mb-8">
        <Button variant="filled" color="blue" onClick={handleDownload}>다운로드</Button>
        <Button variant="outlined" onClick={handleReset}>이전화면</Button>
      </div>
    </div>
  );
};

export default RecordPhotoPrint;